import { useState, useEffect, useCallback, useRef } from "react";
import { makeChoice, nextRound } from "../firebase/roomService";
import { getMyResult } from "../utils/gameLogic";

// ─────────────────────────────────────────────────────────────
// useGame
//
// Manages everything about the current game state:
//   - Tracks the local player's choice
//   - Detects round changes and resets state automatically
//   - Computes the result (win/lose/draw) when both players chose
//   - Exposes actions: choose(), advanceRound()
// ─────────────────────────────────────────────────────────────

const useGame = (roomData, roomCode, playerSlot) => {
  const [myChoice,    setMyChoice]    = useState(null);
  const [showResult,  setShowResult]  = useState(false);

  // Use a ref to track the previous round number
  // This avoids needing trackedRound in the dependency array
  const trackedRoundRef = useRef(null);

  // ── Detect round change → reset local state ───────────────
  useEffect(() => {
    if (!roomData) return;

    const currentRound = roomData.round;

    // First time — just store it, don't reset anything
    if (trackedRoundRef.current === null) {
      trackedRoundRef.current = currentRound;
      return;
    }

    // Round advanced → new round started, reset state
    if (currentRound > trackedRoundRef.current) {
      setMyChoice(null);
      setShowResult(false);
      trackedRoundRef.current = currentRound;
    }
  }, [roomData]); // ✅ fixed: depend on full roomData object

  // ── Detect both players chose → show result panel ─────────
  useEffect(() => {
    if (!roomData) return;
    const p1Chose = !!roomData.p1?.choice;
    const p2Chose = !!roomData.p2?.choice;
    if (p1Chose && p2Chose) {
      setShowResult(true);
    }
  }, [roomData]); // ✅ fixed: depend on full roomData object

  // ── Derived values ─────────────────────────────────────────
  const me  = roomData ? (playerSlot === "p1" ? roomData.p1 : roomData.p2) : null;
  const opp = roomData ? (playerSlot === "p1" ? roomData.p2 : roomData.p1) : null;

  const myStoredChoice  = me?.choice  || null;
  const oppStoredChoice = opp?.choice || null;
  const bothChose       = !!myStoredChoice && !!oppStoredChoice;

  // Result from this player's perspective: "win" | "lose" | "draw" | null
  const result = bothChose
    ? getMyResult(myStoredChoice, oppStoredChoice)
    : null;

  const oppReady = !!oppStoredChoice;
  const locked   = !!myChoice;

  // ── Actions ────────────────────────────────────────────────

  // Called when player clicks Rock, Paper, or Scissors
  const choose = useCallback(async (choice) => {
    if (myChoice || !roomCode || !playerSlot) return;
    setMyChoice(choice);
    await makeChoice(roomCode, playerSlot, choice);
  }, [myChoice, roomCode, playerSlot]); // ✅ all dependencies listed

  // Called by host (p1) to advance to next round
  const advanceRound = useCallback(async () => {
    if (playerSlot !== "p1" || !roomData || !roomCode) return;
    setMyChoice(null);
    setShowResult(false);
    await nextRound(roomCode, roomData);
  }, [playerSlot, roomData, roomCode]); // ✅ all dependencies listed

  return {
    myChoice,
    myStoredChoice,
    oppStoredChoice,
    bothChose,
    result,
    showResult,
    locked,
    oppReady,
    me,
    opp,
    choose,
    advanceRound,
  };
};

export default useGame;